<template>
  <div class="nested-view">
    <section class="section">
      <div class="brand">
        <div class="news" v-if="news.length > 0">
          <div
            class="news-item"
            v-for="(item) in news"
            :key="`news#${item.newsId}`"
          >
            <div class="card" @click="selectNewsItem(item)">
              <div v-if="item.image" class="fig">
                <img class="fig__img" :src="item.image" :alt="item.title">
                <div v-if="item.video" class="fig__video">
                  <img src="../../../assets/img/play-icon.svg" alt="play-icon">
                </div>
              </div>

              <div class="meta">
                <div class="title">{{ item.title }}</div>
                <div class="date">{{ item.publishTime | moment("calendar") }}</div>
                <div class="content">{{ item.content }}</div>
                <div class="footer">
                  <div class="left" :class="{ like: item.liked }" @click.stop.prevent="toggleLike(item)">
                    <i class="material-icons icon">{{ item.liked ? 'favorite' : 'favorite_border' }}</i>
                    <span class="count">{{ item.likes }}</span>
                  </div>
                  <div class="right">
                    <i class="material-icons icon">remove_red_eye</i>
                    <span class="more">{{ $t('news.learnMore') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="empty" v-else>
          <i class="material-icons">save_alt</i>
          {{ $t('news.noNewsFound') }}
        </div>
      </div>
    </section>
    <powered-by></powered-by>
  </div>
</template>

<script>
import swal from 'sweetalert2';
import { mapState, mapMutations } from 'vuex';
import Axios from '@/helpers/axios';
import PoweredBy from '@/components/PoweredBy.vue';

export default {
  name: 'BrandNews',
  computed: mapState([
    'brand',
    'client',
  ]),
  data() {
    return {
      news: [],
      pagination: {
        total: 1,
        currentPage: 1,
        perPage: 10,
        totalPages: 1,
      },
      isLoading: false,
    };
  },
  mounted() {
    this.getBrandNews();
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    ...mapMutations([
      'setLoader',
    ]),
    getBrandNews(page = 1) {
      if (this.isLoading) return;

      this.isLoading = true;

      if (page === 1) {
        this.setLoader(true);
      }

      const data = {
        p: this.client.phoneType,
        brand_id: this.$route.params.brand,
        dtk: this.client.dtk,
        page,
        perPage: this.pagination.perPage,
      };

      Axios({
        method: 'post',
        url: '/v2/News/GetBrandNews',
        data,
      })
        .then((response) => {
          if (response.data && response.data.code === 500) {
            const newItems = Array.isArray(response.data.data) ? response.data.data : [];
            if (page === 1) {
              this.news = newItems;
            } else {
              this.news = [...this.news, ...newItems];
            }
            if (response.data.pagination) {
              this.pagination = { ...this.pagination, ...response.data.pagination };
            }
          } else {
            swal.fire({
              text: (response.data && response.data.msg) || '',
              confirmButtonText: this.$i18n.t('common.ok'),
              showCancelButton: false,
            });
          }
        })
        .catch(() => {
          swal.fire({
            text: this.$i18n.t('common.error'),
            confirmButtonText: this.$i18n.t('common.ok'),
            showCancelButton: false,
          });
        })
        .finally(() => {
          this.isLoading = false;
          if (page === 1) {
            this.setLoader(false);
          }
        });
    },
    handleScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      if (scrollTop + windowHeight >= documentHeight - 200) {
        if (this.pagination.currentPage < this.pagination.totalPages && !this.isLoading) {
          this.getBrandNews(this.pagination.currentPage + 1);
        }
      }
    },
    selectNewsItem(item) {
      this.$router.push({ name: 'News', params: { token: item.newsId } });
    },
    toggleLike(item) {
      const newsItem = this.news.find(n => n.newsId === item.newsId);
      if (!newsItem) return;

      const previousUserLike = newsItem.liked;
      const previousLikes = Number(newsItem.likes);

      const apiUrl = previousUserLike ? '/v2/News/DislikeNews' : '/v2/News/LikeNews';
      const data = {
        p: this.$parent.client.phoneType,
        brand_id: this.$route.params.brand,
        dtk: this.$parent.client.dtk,
        newsId: item.newsId,
      };

      newsItem.liked = !previousUserLike;
      newsItem.likes = newsItem.liked ? previousLikes + 1 : previousLikes - 1;

      Axios({
        method: 'post',
        url: apiUrl,
        data,
      })
        .then((response) => {
          if (response.data && response.data.code !== 500) {
            newsItem.liked = previousUserLike;
            newsItem.likes = previousLikes;
            swal.fire({
              text: (response.data && response.data.msg) || '',
              confirmButtonText: this.$i18n.t('common.ok'),
              showCancelButton: false,
            });
          }
        })
        .catch(() => {
          newsItem.liked = previousUserLike;
          newsItem.likes = previousLikes;
          swal.fire({
            text: this.$i18n.t('common.error'),
            confirmButtonText: this.$i18n.t('common.ok'),
            showCancelButton: false,
          });
        });
    },
  },
  components: {
    PoweredBy,
  },
};
</script>

<style lang="sass" scoped>
.brand
  padding: 16px

.empty
  padding: 15% 0

  text-align: center
  font-weight: bold
  color: #CFD8DC

  .material-icons
    display: block
    margin-bottom: 12px

    font-size: 5.5rem

.news-item
  margin-bottom: 16px

.card
  overflow: hidden
  border-radius: 6px
  background-color: white
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15)
  cursor: pointer

  &:active
    opacity: .85

.fig
  position: relative
  aspect-ratio: 16 / 9
  overflow: hidden
  background: #f5f5f5

  &__img
    width: 100%
    height: 100%
    object-fit: cover
    display: block

  &__video
    position: absolute
    top: 50%
    left: 50%

    transform: translate(-50%, -50%)

    border-radius: 50%
    background: rgba(51, 51, 51, 0.3)

  &__overlay
    position: absolute
    top: 0
    right: 0
    bottom: 0
    left: 0
    display: flex
    align-items: center
    justify-content: center

  &__play
    font-size: 56px
    color: rgba(255, 255, 255, 0.9)

.meta
  padding: 16px
  text-align: center

.title
  margin-bottom: 4px
  font-size: 16px
  font-weight: bold
  color: #333

.date
  margin-bottom: 16px
  font-size: 12px
  color: #8e8e8e

.content
  font-size: 14px
  line-height: 1.6
  color: #8e8e8e
  text-align: left
  display: -webkit-box
  -webkit-box-orient: vertical
  -webkit-line-clamp: 5
  overflow: hidden

.footer
  display: flex
  justify-content: space-between
  align-items: center
  margin-top: 10px
  font-size: 14px

  .icon
    font-size: 24px
    vertical-align: middle
    margin-right: 6px
    color: #bdbdbd

  .left,
  .right
    display: flex
    align-items: center
    font-weight: 600
    cursor: pointer

  .count
    color: #bdbdbd
    font-weight: 600

  .left.like
    .icon,
    .count
      color: #e53935

  .more
    color: #8e8e8e
</style>
